import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const ConversionModal = ({ isOpen, onClose, request, technicians, onConfirm }) => {
  const [conversionData, setConversionData] = useState({
    assignedTechnicians: [],
    scheduledDate: '',
    scheduledTime: '',
    estimatedDuration: '',
    notes: ''
  });

  const [isConverting, setIsConverting] = useState(false);

  const durationOptions = [
    { value: '1', label: '1 hour' },
    { value: '2', label: '2 hours' },
    { value: '3', label: '3 hours' },
    { value: '4', label: '4 hours' },
    { value: '6', label: '6 hours' },
    { value: '8', label: 'Full day (8 hours)' }
  ];

  const handleConvert = () => {
    setIsConverting(true);
    setTimeout(() => {
      onConfirm({
        requestId: request?.id,
        ...conversionData
      });
      setIsConverting(false);
      handleClose();
    }, 1500);
  };

  const handleClose = () => {
    setConversionData({
      assignedTechnicians: [],
      scheduledDate: '',
      scheduledTime: '',
      estimatedDuration: '',
      notes: ''
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-xl shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <div>
              <h2 className="text-xl font-heading font-semibold text-foreground">Convert to Ticket</h2>
              <p className="text-sm text-muted-foreground">Request ID: {request?.requestId}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="bg-muted/30 rounded-lg p-4">
            <h3 className="text-sm font-caption font-medium text-muted-foreground mb-3">Request Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Customer</p>
                <p className="font-medium text-foreground">{request?.customerName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Service Type</p>
                <p className="font-medium text-foreground">{request?.serviceType}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Priority</p>
                <p className="font-medium text-foreground capitalize">{request?.priority}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Products/Services</p>
                <p className="font-medium text-foreground">{request?.productsCount} items</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <Select
              label="Assign Technicians"
              placeholder="Select one or more technicians"
              description="Multiple technicians can be assigned to this ticket"
              required
              multiple
              searchable
              options={technicians}
              value={conversionData?.assignedTechnicians}
              onChange={(value) => setConversionData(prev => ({ ...prev, assignedTechnicians: value }))}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <Input
                label="Scheduled Date"
                type="date"
                required
                value={conversionData?.scheduledDate}
                onChange={(e) => setConversionData(prev => ({ ...prev, scheduledDate: e?.target?.value }))}
                min={new Date()?.toISOString()?.split('T')?.[0]}
              />
              <Input
                label="Scheduled Time"
                type="time"
                required
                value={conversionData?.scheduledTime}
                onChange={(e) => setConversionData(prev => ({ ...prev, scheduledTime: e?.target?.value }))}
              />
            </div>

            <Select
              label="Estimated Duration"
              placeholder="Select estimated time"
              description="Approximate time needed to complete the service"
              required
              options={durationOptions}
              value={conversionData?.estimatedDuration}
              onChange={(value) => setConversionData(prev => ({ ...prev, estimatedDuration: value }))}
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Additional Notes
              </label>
              <textarea
                className="w-full min-h-[100px] px-4 py-3 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-y transition-smooth"
                placeholder="Add any special instructions or notes for the technicians..."
                value={conversionData?.notes}
                onChange={(e) => setConversionData(prev => ({ ...prev, notes: e?.target?.value }))}
              />
              <p className="text-xs text-muted-foreground">Optional</p>
            </div>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground mb-1">Conversion Process</p>
                <p className="text-sm text-muted-foreground">
                  Converting this request will create a new ticket, notify assigned technicians via email and smartphone, 
                  and update the customer with the scheduled service details.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-6 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={handleClose}
            iconName="X"
            iconPosition="left"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleConvert}
            loading={isConverting}
            disabled={
              conversionData?.assignedTechnicians?.length === 0 ||
              !conversionData?.scheduledDate ||
              !conversionData?.scheduledTime ||
              !conversionData?.estimatedDuration
            }
            iconName="CheckCircle"
            iconPosition="right"
            className="flex-1"
          >
            {isConverting ? 'Converting...' : 'Convert to Ticket'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConversionModal;